import { useCallback, useEffect, useRef, useState } from 'react';
import { isTipsEnabled, subscribeTipsChanged } from '../utils/tipsPreference';
import { isOnboardingDone } from './useGettingStarted';
import { track } from '../utils/analytics';

// Deux gestes que rien n'indique a l'ecran : l'appui long sur un nom de corde
// et le tap sur un numero de frette. L'ordre compte, celui des cordes repond au
// trop-plein d'informations dont se plaignent les debutants.
export const DISCOVERY_TIPS = ['strings', 'frets'];

export const OPEN_COUNT_KEY = 'appOpenCount';
export const DISCOVERY_SEEN_KEY = 'discoveryTipsSeen';

// Deuxieme ouverture au plus tot : la premiere appartient au guide.
const MIN_OPEN_COUNT = 2;
// On laisse jouer avant d'ouvrir la bouche.
const DISCOVERY_DELAY_MS = 20000;

function readSeen() {
  try {
    return (localStorage.getItem(DISCOVERY_SEEN_KEY) || '').split(',').filter(Boolean);
  } catch {
    return [];
  }
}

function markSeen(tip) {
  const seen = readSeen();
  if (seen.includes(tip)) return;
  try {
    localStorage.setItem(DISCOVERY_SEEN_KEY, [...seen, tip].join(','));
  } catch {
    // stockage indisponible : le tip pourra revenir, sans casse
  }
}

// Le compteur d'ouvertures s'incremente une fois par lancement, pas une fois
// par montage : StrictMode monte deux fois en developpement.
let openCountForSession = null;

function bumpOpenCount() {
  if (openCountForSession !== null) return openCountForSession;
  let count = 1;
  try {
    count = Number(localStorage.getItem(OPEN_COUNT_KEY) || 0) + 1;
    localStorage.setItem(OPEN_COUNT_KEY, String(count));
  } catch {
    // stockage indisponible : on reste a la premiere ouverture, aucun tip
  }
  openCountForSession = count;
  return count;
}

export function resetOpenCountForTests() {
  openCountForSession = null;
}

/**
 * Tips de decouverte, apres le guide seulement : un seul par session, jamais
 * dans les premieres secondes, chacun une seule fois dans la vie.
 *
 * Le compte a rebours ne tourne que sur l'ecran Play panneau ferme, sinon le
 * tip serait marque vu sans avoir ete lu.
 */
export default function useDiscoveryTips({ currentPage = 'home', activePanelId = null } = {}) {
  const [discoveryTip, setDiscoveryTip] = useState(null);
  const shownRef = useRef(false);

  const openCountRef = useRef(null);
  if (openCountRef.current === null) openCountRef.current = bumpOpenCount();

  const visible = currentPage === 'home' && activePanelId === null;

  useEffect(() => {
    if (shownRef.current || !visible) return undefined;
    if (!isTipsEnabled() || !isOnboardingDone()) return undefined;
    if (openCountRef.current < MIN_OPEN_COUNT) return undefined;

    const seen = readSeen();
    const next = DISCOVERY_TIPS.find((tip) => !seen.includes(tip));
    if (!next) return undefined;

    const timer = setTimeout(() => {
      shownRef.current = true;
      markSeen(next);
      track('discovery_tip_shown', { tip: next });
      setDiscoveryTip(next);
    }, DISCOVERY_DELAY_MS);
    return () => clearTimeout(timer);
  }, [visible]);

  // Couper les tips en cours de route efface celui qui est affiche.
  useEffect(() => subscribeTipsChanged((enabled) => {
    if (!enabled) {
      shownRef.current = true;
      setDiscoveryTip(null);
    }
  }), []);

  const dismissDiscoveryTip = useCallback(() => setDiscoveryTip(null), []);

  return { discoveryTip, dismissDiscoveryTip };
}
